import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { IPermissionCategory, IRole, IRolePUT, UserApiService } from 'shared';

import { AppStateService } from '../../../mtp-common/services/state';

@Injectable({
    providedIn: 'root',
})
export class RoleManagementService {
    private readonly userApi = inject(UserApiService);
    private readonly appState = inject(AppStateService);

    private cachedRoles$: Observable<Array<IRole>> | undefined;
    private cachedCategories$: Observable<Array<IPermissionCategory>> | undefined;

    public constructor() {
        this.appState.activeTenant.distinctStream.subscribe(() => {
            this.cachedRoles$ = null;
            this.cachedCategories$ = null;
        });
    }

    public listRoles(): Promise<Array<IRole>> {
        if (!this.cachedRoles$) {
            this.cachedRoles$ = this.userApi.getRoles().pipe(
                shareReplay(1),
                catchError(() => {
                    delete this.cachedRoles$;
                    return of([]);
                })
            );
        }

        return firstValueFrom(this.cachedRoles$);
    }

    public getRole(roleId: string): Promise<IRole> {
        return this.listRoles().then((roles) => roles.find((role) => role.id === roleId));
    }

    public getPermissionCategories(): Observable<Array<IPermissionCategory>> {
        if (!this.cachedCategories$) {
            this.cachedCategories$ = this.userApi.getPermissionCategories().pipe(
                shareReplay(1),
                catchError(() => {
                    delete this.cachedCategories$;
                    return of([]);
                })
            );
        }

        return this.cachedCategories$;
    }

    public createRole(roleName: string, permissionIds: Array<string> = []): Observable<IRole> {
        return this.userApi.createRole({ name: roleName, featureIds: [], permissionIds }).pipe(
            tap((response) => {
                if (response) delete this.cachedRoles$;
            })
        );
    }

    public removeRole(roleId: string): Observable<IRole> {
        return this.userApi.deleteRole(roleId).pipe(tap(() => delete this.cachedRoles$));
    }

    public updateRole(roleId: string, request: IRolePUT): Observable<IRole> {
        return this.userApi.putRole(roleId, request).pipe(tap(() => delete this.cachedRoles$));
    }
}
